import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, MessageCircle, Users, Trophy, GraduationCap, Clock, Award, Sparkles, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '@/components/Footer';

const API = window.location.origin;

const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showVerificationCode, setShowVerificationCode] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const { user, login, register, loginWithGoogle } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }

    // Check for verification success in URL
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    const error = urlParams.get('error');

    if (verified === 'success') {
      toast.success('Email verified successfully! 🎉', {
        description: 'You can now login to your account',
        duration: 5000
      });
      setShowAuth(true); // Open login dialog
      // Clean URL
      window.history.replaceState({}, '', '/');
    } else if (verified === 'already') {
      toast.success('Email already verified!', {
        description: 'You can login to your account',
        duration: 5000
      });
      setShowAuth(true);
      window.history.replaceState({}, '', '/');
    } else if (error === 'invalid_verification_link') {
      toast.error('Invalid verification link', {
        description: 'Please try registering again or contact support',
        duration: 5000
      });
      window.history.replaceState({}, '', '/');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await login(formData.email, formData.password);
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      // Check if error is about email verification
      if (result.error && result.error.includes('verify your email')) {
        toast.error(result.error, {
          description: "📧 Check your spam/junk folder. Click below to resend the email.",
          duration: 10000,
          action: {
            label: "Resend Email",
            onClick: () => handleResendVerification(formData.email)
          }
        });
      } else {
        toast.error(result.error);
      }
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 5) {
      toast.error('Please enter a valid 5-digit code');
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await axios.post(`${API}/api/auth/verify-code`, {
        email: registeredEmail,
        code: verificationCode
      });
      
      toast.success('Email verified! 🎉', {
        description: 'Logging you in...',
        duration: 2000
      });

      // Log the user in
      const loginResult = await login(registeredEmail, formData.password);
      if (loginResult.success) {
        navigate('/dashboard');
      } else {
        toast.error('Verification successful, but login failed. Please try logging in manually.');
        setShowAuth(false);
        setShowVerificationCode(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResendVerification = async (email) => {
    try {
      const response = await axios.post(`${API}/api/auth/resend-verification`, { email });
      toast.success(response.data.message, {
        description: "📧 Check your spam/junk folder if you don't see it",
        duration: 8000
      });
      setVerificationCode(''); // Clear the code field
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification code');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const result = await register(formData.email, formData.password, formData.name);
    if (result.success) {
      if (result.verification_required) {
        setRegisteredEmail(formData.email);
        setShowVerificationCode(true); // Show code input in the same dialog
        toast.success('Verification code sent!', {
          description: '📧 Check your email (and spam folder) for your 5-digit code',
          duration: 6000
        });
      } else {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } else {
      toast.error(result.error);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail || !newPassword) {
      toast.error('Please enter your email and new password');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setResetting(true);
    try {
      console.log('Resetting password for:', resetEmail);
      console.log('API URL:', `${API}/api/auth/reset-password`);
      const response = await axios.post(`${API}/api/auth/reset-password`, {
        email: resetEmail,
        new_password: newPassword
      });
      console.log('Reset response:', response.data);
      toast.success('Password reset successfully! You can now login.');
      setResetPasswordOpen(false);
      setResetEmail('');
      setNewPassword('');
    } catch (error) {
      console.error('Reset error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || error.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  if (showAuth) {
    return (
      <div className="min-h-screen theme-bg flex items-center justify-center p-4">
        <Card className={`card-glow w-full max-w-md shadow-2xl ${isDarkMode ? 'bg-black/90 border-amber-700' : 'bg-white border-amber-200'}`} data-testid="auth-card">
          <CardHeader className="space-y-1">
            <CardTitle className={`text-3xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Welcome to Lyceum</CardTitle>
            <CardDescription className={`${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Sign in to continue your learning journey</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login" className="space-y-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" data-testid="login-submit-btn">
                    Sign In
                  </Button>
                </form>
                
                <button
                  type="button"
                  onClick={() => setResetPasswordOpen(true)}
                  className="text-sm text-amber-600 hover:text-amber-800 underline text-center w-full mt-2"
                >
                  Forgot Password?
                </button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className={`w-full border-t ${isDarkMode ? 'border-amber-700' : 'border-amber-200'}`} />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className={`px-2 ${isDarkMode ? 'bg-black text-amber-400' : 'bg-white text-amber-600'}`}>Or continue with</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className={`w-full ${isDarkMode ? 'border-amber-600 hover:bg-amber-900/30 text-amber-300' : 'border-amber-300 hover:bg-amber-50'}`}
                  onClick={loginWithGoogle}
                  data-testid="google-login-btn"
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </TabsContent>
              
              <TabsContent value="register" className="space-y-4">
                {!showVerificationCode ? (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="register-password"
                          data-testid="register-password-input"
                          type={showRegisterPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" data-testid="register-submit-btn">
                      Create Account
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center space-y-2">
                      <div className="p-3 bg-green-50 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                        <GraduationCap className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-lg">Check Your Email!</h3>
                      <p className="text-sm text-gray-600">
                        We've sent a 5-digit verification code to:
                      </p>
                      <p className="font-semibold text-amber-900">{registeredEmail}</p>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800 flex items-center gap-2">
                        <span>📧</span>
                        <span>Check your spam/junk folder if you don't see the email</span>
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="verification-code">Enter 5-Digit Code</Label>
                      <Input
                        id="verification-code"
                        type="text"
                        placeholder="12345"
                        maxLength={5}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        className="text-center text-2xl tracking-widest font-mono"
                        autoFocus
                      />
                    </div>
                    
                    <Button
                      onClick={handleVerifyCode}
                      disabled={verifyingCode || verificationCode.length !== 5}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      {verifyingCode ? 'Verifying...' : 'Verify & Continue'}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => handleResendVerification(registeredEmail)}
                      className="text-sm text-amber-600 hover:text-amber-800 underline text-center w-full"
                    >
                      Didn't receive the code? Resend
                    </button>
                  </div>
                )}
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className={`w-full border-t ${isDarkMode ? 'border-amber-700' : 'border-amber-200'}`} />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className={`px-2 ${isDarkMode ? 'bg-black text-amber-400' : 'bg-white text-amber-600'}`}>Or continue with</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className={`w-full ${isDarkMode ? 'border-amber-600 hover:bg-amber-900/30 text-amber-300' : 'border-amber-300 hover:bg-amber-50'}`}
                  onClick={loginWithGoogle}
                  data-testid="google-register-btn"
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </TabsContent>
            </Tabs>
            
            <Button
              variant="ghost"
              className={`w-full mt-4 ${isDarkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-700 hover:text-amber-800'}`}
              onClick={() => setShowAuth(false)}
              data-testid="back-to-landing-btn"
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
        
        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordOpen} onOpenChange={(open) => {
          setResetPasswordOpen(open);
          if (!open) {
            // Clear form fields when dialog closes
            setResetEmail('');
            setNewPassword('');
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Enter your email and a new password to reset your account.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="reset-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="reset-password"
                    type={showResetPassword ? "text" : "password"}
                    placeholder="Enter new password (min 8 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setResetPasswordOpen(false);
                    setResetEmail('');
                    setNewPassword('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetting}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg">
      {/* Header Strip */}
      <header className={`sticky top-0 z-50 ${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/90 border-amber-200'} backdrop-blur-md border-b shadow-sm`}>
        <div className="container mx-auto px-4">
          <nav className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <GraduationCap className={`w-8 h-8 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
              <span className={`text-2xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Lyceum</span>
            </div>
            <Button
              onClick={() => setShowAuth(true)}
              className="bg-amber-600 hover:bg-amber-700 shadow-md"
              data-testid="nav-signin-btn"
            >
              Login / Sign Up
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16 page-enter">

        <div className="text-center max-w-5xl mx-auto mb-20">
          <h1 className={`text-5xl sm:text-6xl lg:text-7xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'} mb-6 leading-tight`}>
            Master History Through
            <span className="block mt-2 gradient-text pb-2">AI-Powered Learning</span>
          </h1>
          
          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            {/* Custom Courses Card */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl border-2 border-amber-200 card-glow card-interactive shadow-lg opacity-0 animate-slide-up animate-stagger-1">
              <div className="flex items-center justify-center w-12 h-12 bg-amber-600 rounded-xl mb-4 mx-auto">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-amber-900 mb-3">Personalized Courses</h3>
              <p className="text-amber-800 leading-relaxed">
                Create tailored history courses on topics you're passionate about. Professor Clio guides you through every lesson with AI-powered insights and interactive quizzes.
              </p>
            </div>
            
            {/* Debate Room Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200 card-glow card-interactive opacity-0 animate-slide-up animate-stagger-2">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-600 rounded-xl mb-4 mx-auto">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Debate Room</h3>
              <p className="text-blue-800 leading-relaxed">
                Sharpen critical thinking through structured debates. Master evidence-based arguments, logical reasoning, and persuasive communication.
              </p>
            </div>
          </div>
          
          <p className={`text-lg font-semibold ${isDarkMode ? 'text-amber-200' : 'text-amber-900'} mb-8`}>
            7-Day Free Trial • Then $9.99/month
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => setShowAuth(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg shadow-xl btn-press pulse-subtle"
              data-testid="get-started-btn"
            >
              Start 7-Day Free Trial
              <Sparkles className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Quote Section */}
        <div className="mb-20 max-w-4xl mx-auto">
          <Card className="card-glow bg-gradient-to-r from-amber-100 to-orange-100 border-amber-300 shadow-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-2xl md:text-3xl font-serif italic text-amber-900 mb-4">
                "Those who cannot remember the past are doomed to repeat it"
              </p>
              <p className="text-lg text-amber-700 font-semibold">
                — George Santayana
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          <Card className="card-glow card-interactive border-amber-300 bg-gradient-to-br from-white to-amber-50 shadow-xl text-center opacity-0 animate-slide-up animate-stagger-1" data-testid="feature-courses-card">
            <CardHeader className="items-center">
              <div className="p-3 rounded-full bg-amber-100 mb-2">
                <BookOpen className="w-10 h-10 text-amber-600" />
              </div>
              <CardTitle className="text-amber-900">Curated Courses</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-700">Comprehensive curricula covering major historical periods with novice to scholar levels.</p>
            </CardContent>
          </Card>

          <Card className="card-glow card-interactive border-amber-300 bg-gradient-to-br from-white to-amber-50 shadow-xl text-center opacity-0 animate-slide-up animate-stagger-2" data-testid="feature-debate-card">
            <CardHeader className="items-center">
              <div className="p-3 rounded-full bg-amber-100 mb-2">
                <Users className="w-10 h-10 text-amber-600" />
              </div>
              <CardTitle className="text-amber-900">Debate Room</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-700">Sharpen critical thinking and rhetorical skills by debating historical topics with evidence-based arguments.</p>
            </CardContent>
          </Card>

          <Card className="card-glow card-interactive border-amber-300 bg-gradient-to-br from-white to-amber-50 shadow-xl text-center opacity-0 animate-slide-up animate-stagger-3" data-testid="feature-ai-chat-card">
            <CardHeader className="items-center">
              <div className="p-3 rounded-full bg-amber-100 mb-2">
                <MessageCircle className="w-10 h-10 text-amber-600" />
              </div>
              <CardTitle className="text-amber-900">History Chat Room</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-700">Ask anything about history and get detailed, engaging answers from our AI professor.</p>
            </CardContent>
          </Card>

          <Card className="card-glow card-interactive border-amber-300 bg-gradient-to-br from-white to-amber-50 shadow-xl text-center opacity-0 animate-slide-up animate-stagger-4" data-testid="feature-progress-card">
            <CardHeader className="items-center">
              <div className="p-3 rounded-full bg-amber-100 mb-2">
                <Trophy className="w-10 h-10 text-amber-600" />
              </div>
              <CardTitle className="text-amber-900">Track Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-700">Monitor your learning journey with detailed progress tracking and proficiency scores.</p>
            </CardContent>
          </Card>
        </div>

        {/* Learning Levels */}
        <div className="text-center mb-12">
          <h2 className={`text-4xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'} mb-4`}>Choose Your Learning Level</h2>
          <p className={`text-lg ${isDarkMode ? 'text-amber-200' : 'text-amber-800'} mb-12`}>Choose a reading level that fits your learning style</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-20">
          <Card className="card-glow border-blue-300 bg-gradient-to-br from-white to-blue-50 shadow-xl text-center" data-testid="level-normal-card">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-4 bg-blue-100 rounded-full w-fit">
                <BookOpen className="w-8 h-8 text-blue-700" />
              </div>
              <CardTitle className="text-2xl text-blue-900">Normal</CardTitle>
              <CardDescription className="text-blue-700 font-semibold">Clear & Accessible</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-blue-800 text-center">Perfect for most learners</p>
              <ul className="space-y-2 text-blue-700">
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>4 engaging lessons</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>Clear, conversational language</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>Focus on big picture &amp; key stories</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>Interactive quizzes</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="card-glow border-purple-400 bg-gradient-to-br from-white to-purple-50 shadow-xl text-center" data-testid="level-scholar-card">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-4 bg-purple-100 rounded-full w-fit">
                <Award className="w-8 h-8 text-purple-700" />
              </div>
              <CardTitle className="text-2xl text-purple-900">Scholar</CardTitle>
              <CardDescription className="text-purple-700 font-semibold">Academic & In-Depth</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-purple-800 text-center">For academically-minded learners</p>
              <ul className="space-y-2 text-purple-700">
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>5 comprehensive lessons</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>Scholarly language &amp; terminology</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>Historiographical perspectives</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="mr-2">•</span>
                  <span>In-depth assessments</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-amber-100 to-orange-100 rounded-3xl p-8 sm:p-12 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-amber-900 mb-4">Ready to Begin Your Journey?</h2>
          <p className="text-lg sm:text-xl text-amber-800 mb-2 max-w-2xl mx-auto px-4">
            Join thousands of history enthusiasts learning with AI-powered education
          </p>
          <p className="text-base sm:text-lg font-semibold text-amber-900 mb-8 px-4">
            7-Day Free Trial • Then $9.99/month • Cancel anytime
          </p>
          <div className="w-full">
            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={() => setShowAuth(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 sm:px-12 py-6 text-lg sm:text-xl shadow-xl"
                data-testid="cta-start-learning-btn"
              >
                Start Your Free Trial
                <GraduationCap className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPage;